import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import JSZip from "jszip";
import PayslipEdit from "@/pages/PayslipEdit";
import { RefreshCw, Wallet, TrendingUp, Users, Download, CheckCheck, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Avatar } from "@/components/common";
import { StatusBadge } from "@/components/common";
import { EmptyState } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { money } from "@/lib/format";

export default function Payroll() {
  const nav39 = useNavigate();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const load = async () => { const { data } = await api.get("/payroll", { params: { month } }); setRows(data); };
  useEffect(() => { load(); }, [month]);

  const generate = async () => {
    setBusy(true);
    try { await api.post("/payroll/generate", null, { params: { month } }); toast.success("Payroll generated"); load(); }
    catch (e) { toast.error("Failed to generate"); }
    setBusy(false);
  };
  const setStatus = async (id, status) => { await api.put(`/payroll/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); };

  const shown = rows.filter((r) => `${r.employee_name} ${r.employee_code}`.toLowerCase().includes(q.trim().toLowerCase()));
  const total = rows.reduce((s, r) => s + r.net, 0);
  const paid = rows.filter((r) => r.status === "Paid").length;
  const approved = rows.filter((r) => r.status === "Approved");
  const drafts = rows.filter((r) => r.status === "Draft");
  const [bulk, setBulk] = useState(null);
  const zipRef = useRef(null);
  const approveAll = async () => {
    if (!drafts.length) return toast.info("No Draft payslips");
    if (!window.confirm(`Approve ${drafts.length} Draft payslips?`)) return;
    for (const r of drafts) await api.put(`/payroll/${r.id}/status`, { status: "Approved" });
    toast.success(`${drafts.length} payslips approved`); load();
  };
  const unpaid = rows.filter((r) => r.status !== "Paid");
  const paidAll = async () => {
    if (!unpaid.length) return toast.info("All payslips are already Paid");
    if (!window.confirm(`Mark ${unpaid.length} payslips as Paid?`)) return;
    for (const r of unpaid) await api.put(`/payroll/${r.id}/status`, { status: "Paid" });
    toast.success(`${unpaid.length} payslips marked Paid`); load();
  };
  const downloadAll = () => {
    if (!approved.length) return toast.info("No Approved payslips");
    zipRef.current = new JSZip();
    setBulk({ ids: approved.map((r) => r.id), i: 0 });
  };
  const onBulkPdf = async (blob, name) => {
    zipRef.current.file(name, blob);
    const next = bulk.i + 1;
    if (next < bulk.ids.length) return setBulk({ ...bulk, i: next });
    const out = await zipRef.current.generateAsync({ type: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(out); a.download = `${monthLabel}.zip`; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    toast.success(`Downloaded ${bulk.ids.length} payslips`); setBulk(null);
  };
  const monthLabel = new Date(month + "-01").toLocaleDateString("en-IN", { timeZone: "UTC", month: "long", year: "numeric" });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><p className="text-slate-500">{monthLabel} payroll run</p></div>
        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="payroll-month" className="rounded-xl w-full sm:w-40 bg-white" />
          <Button onClick={approveAll} variant="outline" className="rounded-xl h-10"><CheckCheck className="w-4 h-4 mr-1" /> Approve All</Button>
          <Button onClick={paidAll} variant="outline" className="rounded-xl h-10"><CheckCheck className="w-4 h-4 mr-1" /> Paid All</Button>
          <Button onClick={downloadAll} disabled={!!bulk} className="rounded-xl bg-slate-900 hover:bg-slate-800 h-10"><Download className="w-4 h-4 mr-1" /> {bulk ? `Preparing ${bulk.i + 1}/${bulk.ids.length}...` : `Download All (${approved.length})`}</Button>
          <Button onClick={generate} disabled={busy} data-testid="generate-payroll" className="rounded-xl bg-emerald-600 hover:bg-emerald-700 h-10"><RefreshCw className={`w-4 h-4 mr-1 ${busy ? "animate-spin" : ""}`} /> Generate</Button>
        </div>
      </div>

      {bulk && <div style={{ position: "fixed", left: -10000, top: 0, width: 1000 }}><PayslipEdit key={bulk.ids[bulk.i]} pid={bulk.ids[bulk.i]} readOnly autoPdf onPdf={onBulkPdf} /></div>}

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="sm:col-span-1 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-5 sm:p-6 text-white">
          <div className="flex items-center gap-2 text-slate-300 text-sm mb-2"><Wallet className="w-4 h-4" /> Total Payroll</div>
          <p className="font-heading text-3xl font-bold">{money(total)}</p>
          <p className="text-xs text-slate-400 mt-1">{monthLabel}</p>
        </div>
        <div className="rounded-2xl bg-white border border-slate-200 p-6"><div className="flex items-center gap-2 text-slate-400 text-sm mb-2"><Users className="w-4 h-4" /> Employees</div><p className="font-heading text-3xl font-bold text-slate-900">{rows.length}</p></div>
        <div className="rounded-2xl bg-white border border-slate-200 p-6"><div className="flex items-center gap-2 text-slate-400 text-sm mb-2"><TrendingUp className="w-4 h-4" /> Paid</div><p className="font-heading text-3xl font-bold text-emerald-600">{paid}<span className="text-lg text-slate-300">/{rows.length}</span></p></div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee name or ID..." data-testid="payroll-search" className="rounded-xl bg-white pl-9 h-10" />
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        {rows.length === 0 ? <EmptyState icon={Wallet} title={`No payroll for ${monthLabel}`} subtitle="Click Generate to calculate salaries from attendance and leave." /> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead><tr className="text-left text-slate-500 text-xs">
                <th className="font-medium px-5 py-3">Employee</th><th className="font-medium px-3 py-3 text-right">Salary</th>
                <th className="font-medium px-3 py-3 text-right">LOP</th><th className="font-medium px-3 py-3 text-right">Overtime</th>
                <th className="font-medium px-3 py-3 text-right">Incentive</th><th className="font-medium px-3 py-3 text-right">Deduction</th>
                <th className="font-medium px-3 py-3 text-right">Net Salary</th><th className="font-medium px-5 py-3">Status</th>
              </tr></thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.id} onClick={(ev) => { if (!ev.target.closest("button,input,select,[role=combobox],[role=option],[role=listbox]")) nav39(`/payroll/${r.id}`); }} className="cursor-pointer border-t border-slate-50 hover:bg-slate-50/80 transition-colors" data-testid={`payroll-row-${r.employee_code}`}>
                    <td className="px-5 py-3"><div className="flex items-center gap-2.5"><Avatar name={r.employee_name} size={30} /><div><p className="font-medium text-slate-700 leading-tight">{r.employee_name}</p><p className="text-xs text-slate-400">{r.employee_code}</p></div></div></td>
                    <td className="px-3 py-3 text-right text-slate-600">{money(r.salary)}</td>
                    <td className="px-3 py-3 text-right text-red-500">{r.lop ? `-${money(r.lop)}` : "—"}</td>
                    <td className="px-3 py-3 text-right text-emerald-600">{r.overtime ? `+${money(r.overtime)}` : "—"}</td>
                    <td className="px-3 py-3 text-right text-emerald-600">{r.incentive ? `+${money(r.incentive)}` : "—"}</td>
                    <td className="px-3 py-3 text-right text-red-500">{r.deduction ? `-${money(r.deduction)}` : "—"}</td>
                    <td className="px-3 py-3 text-right font-semibold text-slate-900">{money(r.net)}</td>
                    <td className="px-5 py-3">
                      <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                        <SelectTrigger className="rounded-lg h-8 w-28 text-xs" data-testid={`payroll-status-${r.employee_code}`}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="Draft">Draft</SelectItem><SelectItem value="Approved">Approved</SelectItem><SelectItem value="Paid">Paid</SelectItem></SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {shown.length === 0 && <p className="text-center text-sm text-slate-400 py-8">No employee matches "{q}"</p>}
          </div>
        )}
      </div>
    </div>
  );
}
